import { LineStyle } from './styling';

export class DataSourceModel {
    layers: LayerBasicModel[];
}

export class LayerBasicModel {
    id: number;
    name: string;
}

export class LayerModel {
    fields: FieldFormat[];
}

export class FieldFormat {
    name: string;
    format: string;

    constructor(name: string, format: string) {
        this.name = name;
        this.format = format;
    }
}

export class RouteConfiguration {
    childrenAttribute: string;
    children: RouteHierarchyItem[];
    subRouteItems: SubRouteItem[];
}

export class RouteHierarchyItem {
    childrenAttribute?: string;
    value: string;
    children?: RouteHierarchyItem[];
    routeId: number;
    routeName: string;
    styleOverride?: LineStyle;
    hidden?: boolean;
    configId?: number;

    public static routeHierarchyItemCompareFn = (a, b) => a.value.localeCompare(b.value);

    static getVisibleItems(hierarchyItems: RouteHierarchyItem[], routeConfigId: number): RouteHierarchyItem[] {
        const visibleItems = hierarchyItems
            .filter(it => !it.hidden)
            .sort(RouteHierarchyItem.routeHierarchyItemCompareFn);
        visibleItems.forEach(it => {
            if (it.children && it.children.length > 0) {
                it.children = RouteHierarchyItem.getVisibleItems(it.children, routeConfigId);
            } else {
                it.configId = routeConfigId;
            }
        });
        return visibleItems;
    }

    static getItemsNoOverrides(hierarchyItems: RouteHierarchyItem[], routeConfigId: number): RouteHierarchyItem[] {
      const itemsWithNoOverrides = hierarchyItems
        .map(it => {
          delete it.styleOverride;
          return it;
        })
        .sort(RouteHierarchyItem.routeHierarchyItemCompareFn);
      itemsWithNoOverrides.forEach(it => {
        if (it.children && it.children.length > 0) {
          it.children = RouteHierarchyItem.getItemsNoOverrides(it.children, routeConfigId);
        } else {
          it.configId = routeConfigId;
        }
      });
      return itemsWithNoOverrides;
    }

    static getFlat(levels: number, hierarchyItems: RouteHierarchyItem[]): RouteHierarchyItem[] {
        const selectedItems: RouteHierarchyItem[] = [];
        selectedItems.push(...hierarchyItems);
        let levelItems: RouteHierarchyItem[] = hierarchyItems;
        for (let i = levels - 1; i > 0; i--) {
            levelItems = levelItems
                .filter(item => !!item.children)
                .map(items => items.children)
                .reduce((accumulator, value) => accumulator.concat(value), []);
            selectedItems.push(...levelItems);
        }
        return selectedItems;
    }

    static getLeaves(levels: number, hierarchyItems: RouteHierarchyItem[]): RouteHierarchyItemWithPath[] {
        let selectedItems: RouteHierarchyItemWithPath[] = hierarchyItems.filter(item => !item.hidden);
        for (let i = levels - 1; i > 0; i--) {
            selectedItems = selectedItems
                .filter(item => !item.hidden)
                .map(item => {
                    return (item.children as RouteHierarchyItemWithPath[]).map(oldChild => {
                        const child = Object.assign({}, oldChild);
                        if (!child.path) {
                            child.path = !item.path ? [] : [...item.path];
                        }
                        child.path.push(item.value);
                        return child;
                    });
                })
                .reduce((accumulator, value) => accumulator.concat(value), []);
        }
        return selectedItems.filter(item => !item.hidden);
    }
    static getLeavesWithExplicitColors(levels: number, hierarchyItems: RouteHierarchyItem[]): RouteHierarchyItemWithPath[] {
        let selectedItems: RouteHierarchyItemWithPath[] = JSON.parse(JSON.stringify(hierarchyItems));
        for (let i = levels - 1; i > 0; i--) {
            selectedItems = selectedItems
                .map(item => {
                    // pass color downstream if not overridden
                    if (item.styleOverride?.color) {
                        item.children.forEach(child => {
                            if (!child.styleOverride?.color) {
                              if (child.styleOverride) {
                                child.styleOverride.color = item.styleOverride.color;
                              } else {
                                child.styleOverride = { color: item.styleOverride.color };
                              }
                            }
                        });
                    }
                    if (item.styleOverride?.lineType) {
                        item.children.forEach(child => {
                            if (!child.styleOverride?.lineType) {
                              if (child.styleOverride) {
                                child.styleOverride.lineType = item.styleOverride.lineType;
                              } else {
                                child.styleOverride = { lineType: item.styleOverride.lineType };
                              }
                            }
                        });
                    }
                    if (item.styleOverride?.width) {
                      item.children.forEach(child => {
                        if (!child.styleOverride?.width) {
                          if (child.styleOverride) {
                            child.styleOverride.width = item.styleOverride.width;
                          } else {
                            child.styleOverride = { width: item.styleOverride.width };
                          }
                        }
                      });
                    }
                    if (item.styleOverride?.opacity) {
                      item.children.forEach(child => {
                        if (!child.styleOverride?.opacity) {
                          if (child.styleOverride) {
                            child.styleOverride.opacity = item.styleOverride.opacity;
                          } else {
                            child.styleOverride = { opacity: item.styleOverride.opacity };
                          }
                        }
                      });
                    }
                    return item.children.map(kid => {
                        const child = kid as RouteHierarchyItemWithPath;
                        const parent = item as RouteHierarchyItemWithPath;
                        if (!child.path) {
                            child.path = !parent.path ? [] : [...parent.path];
                        }
                        child.path.push(item.value);
                        return child;
                    });
                })
                .reduce((accumulator, value) => accumulator.concat(value), []);
        }
        return selectedItems;
    }
    static getByPath(path: string[], hierarchyItems: RouteHierarchyItem[]): RouteHierarchyItem {
        let items = hierarchyItems;
        let item: RouteHierarchyItem;
        for (const value of path) {
            item = items.find(hierarchyItem => hierarchyItem.value === value);
            if (!item) {
                return null;
            } else {
                items = item.children;
            }
        }
        return item;
    }
    static getPathLabel(path: string[], hierarchyItems: RouteHierarchyItem[]): string {
        let items = hierarchyItems;
        let item: RouteHierarchyItem;
        let label = '';
        for (const [index, pathValue] of path.entries()) {
            item = items.find(hierarchyItem => hierarchyItem.value === pathValue);
            if (!item) {
                return label;
            } else {
                // apply only for last string in path
                if (path.length === index + 1) {
                    if (index > 0) {
                        label = label + '.. / ';
                    }
                    if (item.routeId === -1) {
                        label = label + item.routeName;
                    } else {
                        label = label + item.value;
                    }
                }
                items = item.children;
            }
        }
        return label;
    }
}

export class RouteConfigurationWithSchema {
    id?: number;
    schemaUpdated: Date;
    configurationGenerated: Date;
    updateNote?: string;

    constructor(
        public name: string,
        public source: RouteSourceType,
        public url: string,
        public auth: RouteConfigurationAuth,
        public layerId: number,
        public layerName: string,
        public regularUpdate: boolean,
        public updateCustomLayers: boolean,
        public schema: RouteSchema,
        public configuration: RouteConfiguration,
    ) {}

    public static routeConfigurationCompareFn = (a, b) => a.name.localeCompare(b.name);
}

export class RouteSchema {
    constructor(
        public classification: RouteImportAttribute[],
        public subRouteAttribute: RouteImportAttribute,
        public style: LineStyle,
    ) {}
}

export class RouteImportAttribute {
    constructor(
        public attribute: string
    ) {}
}



export class SubRouteItem {
  value: string;
  subRouteId: number;
  style?: LineStyle;
}

export class RouteHierarchyItemWithPath extends RouteHierarchyItem {
    path?: string[];
}

export enum RouteSourceType {
    SHAPE_FILE = 'SHAPE',
    ARCGIS_SERVICE = 'ARCGIS',
}

export enum TaskStatus {
    IN_PROGRESS= 'IN_PROGRESS',
    DONE = 'DONE',
    FAILURE = 'FAILURE',
    UNKNOWN = 'UNKNOWN',
}

export enum GenerateTokenClient {
    REFERER = 'referer',
    REQUESTIP = 'requestip',
}

export class RouteConfigurationAuth {
    constructor(
      public username: string,
      public password: string,
      public client: GenerateTokenClient,
    ) {
    }
}

export class RouteConfigurationChangeLog {
  readonly id: number;
  readonly created: Date;
  readonly changedBy: string;
  readonly changes: RouteConfigurationChanges;
  readonly note: string;
}

export class RouteConfigurationChanges {
  readonly configuration: any;
  readonly geometry: any;
}
